import { useContext, useEffect, useState } from "react";
import { collection, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { AuthContext } from "../AuthContext";

export default function AdminFeedback() {
  const { isAdmin, loading: authLoading } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState("");
  const [hideArchived, setHideArchived] = useState(true);
  const [sortBy, setSortBy] = useState("dateDesc");

  useEffect(() => {
    if (authLoading || !isAdmin) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, "feedback"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authLoading, isAdmin]);

  const setStatus = async (itemId, status) => {
    setActionError("");
    try {
      await updateDoc(doc(db, "feedback", itemId), { status });
      setItems((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, status } : item))
      );
    } catch (err) {
      console.error(err);
      setActionError("Failed to update feedback status.");
    }
  };

  const visibleItems = hideArchived
    ? items.filter((item) => (item.status || "new") !== "archived")
    : items;

  const priorityOrder = {
    high: 1,
    medium: 2,
    low: 3,
  };

  const getPriorityRank = (value) => {
    const key = String(value || "").toLowerCase();
    return priorityOrder[key] || 99;
  };

  const sortedItems = [...visibleItems].sort((a, b) => {
    if (sortBy === "dateAsc" || sortBy === "dateDesc") {
      const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return sortBy === "dateAsc" ? aTime - bTime : bTime - aTime;
    }

    if (sortBy === "priorityAsc" || sortBy === "priorityDesc") {
      const diff = getPriorityRank(a.priority) - getPriorityRank(b.priority);
      return sortBy === "priorityAsc" ? diff : -diff;
    }

    return 0;
  });

  return (
    <div style={{ padding: "1.5rem" }}>
      <h1>Feedback Inbox</h1>
      {authLoading ? (
        <p>Loading...</p>
      ) : !isAdmin ? (
        <p>You do not have access to this page.</p>
      ) : loading ? (
        <p>Loading feedback...</p>
      ) : items.length === 0 ? (
        <p>No feedback yet.</p>
      ) : (
        <>
          {actionError && <p style={{ color: "#b00020" }}>{actionError}</p>}
          <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "0.75rem" }}>
            <label style={{ display: "inline-flex", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={hideArchived}
                onChange={(event) => setHideArchived(event.target.checked)}
              />
              Hide archived
            </label>
            <label style={{ display: "inline-flex", gap: "0.5rem", alignItems: "center" }}>
              Sort by
              <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                <option value="dateDesc">Date (newest first)</option>
                <option value="dateAsc">Date (oldest first)</option>
                <option value="priorityDesc">Priority (high to low)</option>
                <option value="priorityAsc">Priority (low to high)</option>
              </select>
            </label>
          </div>
          {sortedItems.length === 0 ? (
            <p>No feedback matches the current filter.</p>
          ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Priority</th>
              <th style={thStyle}>Class</th>
              <th style={thStyle}>Contact</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Description</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((item) => (
              <tr key={item.id}>
                <td style={tdStyle}>{item.type || "–"}</td>
                <td style={tdStyle}>{item.priority || "–"}</td>
                <td style={tdStyle}>{item.classContext || "–"}</td>
                <td style={tdStyle}>{item.contactEmail || "–"}</td>
                <td style={tdStyle}>{item.status || "new"}</td>
                <td style={tdStyle}>
                  {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : "–"}
                </td>
                <td style={tdStyle}>{item.description || "–"}</td>
                <td style={tdStyle}>
                  {item.status === "archived" ? (
                    <button
                      onClick={() => setStatus(item.id, "new")}
                      style={buttonStyle}
                    >
                      Unarchive
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(item.id, "archived")}
                      style={buttonStyle}
                    >
                      Archive
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          )}
        </>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  padding: "0.5rem",
};

const tdStyle = {
  borderBottom: "1px solid #eee",
  padding: "0.5rem",
  verticalAlign: "top",
};

const buttonStyle = {
  padding: "0.35rem 0.6rem",
  borderRadius: "4px",
  border: "1px solid #999",
  background: "#fff",
  cursor: "pointer",
};
